import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, ShieldCheck, AlertCircle, Clock, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/system/PageHeader';
import { SectionCard } from '@/components/system/SectionCard';
import {
  useIFConviteByToken,
  useIFContratoPreview,
  useIFResgatarConvite,
} from '../hooks/useIFAdminContratos';

const MAX_ATTEMPTS = 5;

const IFResgatarConvitePage: React.FC = () => {
  const navigate = useNavigate();
  const [tokenInput, setTokenInput] = useState('');
  const [submittedToken, setSubmittedToken] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [accepted, setAccepted] = useState(false);

  const { data: convite, isLoading: loadingConvite, isError: errorConvite } = useIFConviteByToken(submittedToken);
  const { data: preview, isLoading: loadingPreview } = useIFContratoPreview(
    convite?.status === 'pendente' ? convite?.contrato_id : null
  );
  const resgatarMutation = useIFResgatarConvite();

  const blocked = attempts >= MAX_ATTEMPTS;

  const handleValidate = () => {
    const cleaned = tokenInput.trim();
    if (!cleaned) { toast.error('Insira o token do convite.'); return; }
    if (blocked) { toast.error('Número máximo de tentativas atingido. Solicite um novo convite.'); return; }
    setAttempts(a => a + 1);
    setSubmittedToken(cleaned);
    setAccepted(false);
  };

  const handleAccept = async () => {
    if (!submittedToken) return;
    try {
      await resgatarMutation.mutateAsync(submittedToken);
      setAccepted(true);
      toast.success('Convite aceito! Acesso provisório concedido ao contrato.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao resgatar convite.');
    }
  };

  const isTokenInvalid = submittedToken && !loadingConvite && (errorConvite || !convite);
  const isExpired = convite && (convite.status !== 'pendente' || new Date(convite.expires_at) < new Date());
  const isValid = convite && convite.status === 'pendente' && new Date(convite.expires_at) >= new Date();

  return (
    <div className="space-y-6">
      <PageHeader
          title="Resgatar Convite"
          subtitle="Insira o token recebido para vincular sua instituição a um contrato"
        />

        <SectionCard title="Token de Convite" icon={ShieldCheck}>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                value={tokenInput}
                onChange={e => setTokenInput(e.target.value)}
                placeholder="Cole o token do convite aqui"
                className="font-mono text-sm"
                disabled={blocked || accepted}
              />
              <Button onClick={handleValidate} disabled={loadingConvite || blocked || accepted}>
                {loadingConvite ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Validar'}
              </Button>
            </div>

            {blocked && (
              <div className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="h-4 w-4" />
                Número máximo de tentativas atingido. Solicite um novo convite ao administrador.
              </div>
            )}

            {attempts > 0 && attempts < MAX_ATTEMPTS && !isValid && (
              <p className="text-xs text-muted-foreground">Tentativa {attempts} de {MAX_ATTEMPTS}</p>
            )}
          </div>
        </SectionCard>

        {isTokenInvalid && !blocked && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">Token inválido</p>
                  <p className="text-sm text-muted-foreground">O token informado não foi encontrado. Verifique e tente novamente.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isExpired && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <Clock className="h-6 w-6 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">Convite expirado ou já utilizado</p>
                  <p className="text-sm text-muted-foreground">
                    {convite?.status === 'aceito' ? 'Este convite já foi utilizado.' :
                     convite?.status === 'revogado' ? 'Este convite foi revogado pelo administrador.' :
                     'Este convite expirou. Solicite um novo convite.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isValid && !accepted && (
          <>
            <SectionCard title="Resumo do Contrato" icon={ShieldCheck}>
              {loadingPreview ? (
                <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
              ) : preview ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Tipo de Acesso</p>
                    <Badge variant={convite.tipo_acesso === 'financiamento' ? 'default' : 'outline'} className="mt-1">
                      {convite.tipo_acesso === 'financiamento' ? 'Financiamento' : 'Monitoramento'}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Cluster Responsável</p>
                    <p className="font-medium mt-1">{preview.cluster_responsavel}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Estágio de Execução</p>
                    <p className="font-medium mt-1">{preview.estagio_execucao}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Classificação de Risco</p>
                    <Badge variant="secondary" className="mt-1">{preview.classificacao_risco}</Badge>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Dados do contrato indisponíveis.</p>
              )}
            </SectionCard>

            <div className="flex justify-end">
              <Button size="lg" onClick={handleAccept} disabled={resgatarMutation.isPending} className="gap-2">
                {resgatarMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
                Aceitar acesso ao contrato
              </Button>
            </div>
          </>
        )}

        {accepted && (
          <Card className="border-primary">
            <CardContent className="pt-6 text-center space-y-4">
              <ShieldCheck className="h-12 w-12 mx-auto" style={{ color: 'hsl(var(--success))' }} />
              <div>
                <p className="text-lg font-semibold">Acesso provisório concedido</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Seu acesso ao contrato é provisório por 14 dias. O administrador do cluster deverá confirmar o vínculo para acesso permanente.
                </p>
              </div>
              <Button onClick={() => navigate('/financiamento/meus-contratos')} className="gap-2">
                <ArrowRight className="h-4 w-4" /> Ir para Meus Contratos
              </Button>
            </CardContent>
          </Card>
        )}
    </div>
  );
};

export default IFResgatarConvitePage;
